/**
 * Geographic Utilities
 *
 * Provides utilities for:
 * - Converting between Google Maps and GeoJSON formats
 * - Point-in-polygon checking
 * - Checking if properties are within buyer zones
 */

import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { point, polygon, multiPolygon } from '@turf/helpers';

const isFiniteLngLat = (coord) => (
    Array.isArray(coord) &&
    coord.length >= 2 &&
    Number.isFinite(coord[0]) &&
    Number.isFinite(coord[1])
);

/**
 * Convert Google Maps coordinates to GeoJSON Polygon
 * @param {Array<{lat: number, lng: number}>} googleCoords - Google Maps format coordinates
 * @returns {Object} - GeoJSON Polygon geometry
 */
export const googleToGeoJSON = (googleCoords) => {
    if (!Array.isArray(googleCoords) || googleCoords.length < 3) {
        return null;
    }

    const coordinates = googleCoords.map(coord => [coord.lng, coord.lat]);

    // Close the polygon if not already closed
    const first = coordinates[0];
    const last = coordinates[coordinates.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
        coordinates.push([...first]);
    }

    return {
        type: "Polygon",
        coordinates: [coordinates]
    };
};

/**
 * Convert GeoJSON Polygon to Google Maps coordinates
 * @param {Object} geoJSON - GeoJSON Polygon geometry
 * @returns {Array<{lat: number, lng: number}>} - Google Maps format coordinates
 */
export const geoJSONToGoogle = (geoJSON) => {
    if (!geoJSON || !geoJSON.coordinates || !geoJSON.coordinates[0]) {
        return [];
    }

    // Convert GeoJSON [lng, lat] to Google Maps {lat, lng}
    return geoJSON.coordinates[0].map(coord => ({
        lat: coord[1],
        lng: coord[0]
    }));
};

/**
 * Convert Polygon/MultiPolygon geometry into render-ready Google polygon paths.
 * Output format: Array<Polygon>, where each Polygon is Array<Ring>, and Ring is Array<{lat,lng}>
 * Compatible with @react-google-maps/api <Polygon paths={...}/> for holes.
 *
 * @param {Object} geoJSONGeometry - GeoJSON Polygon or MultiPolygon geometry
 * @returns {Array<Array<Array<{lat:number,lng:number}>>>}
 */
export const geoJSONToGooglePolygons = (geoJSONGeometry) => {
    if (!geoJSONGeometry?.coordinates) return [];

    const toRing = (ring) => {
        if (!Array.isArray(ring)) return [];
        return ring
            .filter(isFiniteLngLat)
            .map(([lng, lat]) => ({ lat, lng }));
    };

    const sanitizePolygon = (rings) => {
        if (!Array.isArray(rings)) return null;
        const sanitized = rings
            .map(toRing)
            .filter(ring => ring.length >= 3);
        return sanitized.length > 0 ? sanitized : null;
    };

    if (geoJSONGeometry.type === 'Polygon') {
        const polygonRings = sanitizePolygon(geoJSONGeometry.coordinates);
        return polygonRings ? [polygonRings] : [];
    }

    if (geoJSONGeometry.type === 'MultiPolygon') {
        return geoJSONGeometry.coordinates
            .map(polygonCoords => sanitizePolygon(polygonCoords))
            .filter(Boolean);
    }

    return [];
};

/**
 * Compute bounds from render-ready polygon paths.
 *
 * @param {Array<Array<Array<{lat:number,lng:number}>>>} polygons
 * @returns {{ north:number, south:number, east:number, west:number } | null}
 */
export const getBoundsFromGooglePolygons = (polygons) => {
    if (!Array.isArray(polygons) || polygons.length === 0) return null;

    let north = -Infinity;
    let south = Infinity;
    let east = -Infinity;
    let west = Infinity;

    for (const polygonRings of polygons) {
        if (!Array.isArray(polygonRings)) continue;
        for (const ring of polygonRings) {
            if (!Array.isArray(ring)) continue;
            for (const coord of ring) {
                const lat = coord?.lat;
                const lng = coord?.lng;
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
                if (lat > north) north = lat;
                if (lat < south) south = lat;
                if (lng > east) east = lng;
                if (lng < west) west = lng;
            }
        }
    }

    if (![north, south, east, west].every(Number.isFinite)) return null;
    return { north, south, east, west };
};

/**
 * Check if a point is inside a polygon
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @param {Object} geoJSONGeometry - GeoJSON Polygon geometry
 * @returns {boolean}
 */
export const isPointInPolygon = (lat, lng, geoJSONGeometry) => {
    try {
        if (!geoJSONGeometry || !geoJSONGeometry.coordinates) {
            return false;
        }

        const pt = point([lng, lat]);
        const poly = polygon(geoJSONGeometry.coordinates);

        return booleanPointInPolygon(pt, poly);
    } catch (error) {
        console.error('Error checking point in polygon:', error);
        return false;
    }
};

/**
 * Check if a point falls inside a Polygon/MultiPolygon geometry.
 *
 * @param {number} lat
 * @param {number} lng
 * @param {Object} geoJSONGeometry
 * @returns {boolean}
 */
export const isPointInGeometry = (lat, lng, geoJSONGeometry) => {
    try {
        if (!geoJSONGeometry?.coordinates) return false;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
        const pt = point([lng, lat]);

        if (geoJSONGeometry.type === 'Polygon') {
            return booleanPointInPolygon(pt, polygon(geoJSONGeometry.coordinates));
        }
        if (geoJSONGeometry.type === 'MultiPolygon') {
            return booleanPointInPolygon(pt, multiPolygon(geoJSONGeometry.coordinates));
        }
        return false;
    } catch (error) {
        console.error('Error checking point in geometry:', error);
        return false;
    }
};

/**
 * Check if a property is in any of the given zones
 * @param {Object} property - Property with latitude and longitude
 * @param {Array} zones - Array of zone objects with data.geometry
 * @returns {Object} - { isInZone: boolean, matchingZones: Array<{id, name}> }
 */
export const checkPropertyInZones = (property, zones) => {
    if (!property?.latitude || !property?.longitude || !Array.isArray(zones) || zones.length === 0) {
        return { isInZone: false, matchingZones: [] };
    }

    const matchingZones = zones
        .filter(zone => {
            // Skip inactive zones
            if (zone.data?.active === false) return false;
            if (!zone.data?.geometry) return false;

            return isPointInPolygon(
                property.latitude,
                property.longitude,
                zone.data.geometry
            );
        })
        .map(zone => ({
            id: zone.data.id || zone.id,
            name: zone.data.name || 'Unnamed Zone'
        }));

    return {
        isInZone: matchingZones.length > 0,
        matchingZones
    };
};

/**
 * Filter properties that fall within any of the given zones
 * @param {Array} properties - Array of property objects
 * @param {Array} zones - Array of zone objects
 * @returns {Array} - Filtered properties
 */
export const filterPropertiesByZones = (properties, zones) => {
    if (!zones?.length) return properties;

    return properties.filter(property => {
        const { isInZone } = checkPropertyInZones(property, zones);
        return isInZone;
    });
};

/**
 * Calculate the center point of a polygon
 * @param {Object} geoJSONGeometry - GeoJSON Polygon geometry
 * @returns {Object|null} - {lat, lng} center point or null
 */
export const getPolygonCenter = (geoJSONGeometry) => {
    if (!geoJSONGeometry || !geoJSONGeometry.coordinates || !geoJSONGeometry.coordinates[0]) {
        return null;
    }

    const coords = geoJSONGeometry.coordinates[0];
    let sumLat = 0;
    let sumLng = 0;

    // Exclude the closing point (same as first)
    const count = coords.length - 1;

    for (let i = 0; i < count; i++) {
        sumLng += coords[i][0];
        sumLat += coords[i][1];
    }

    return {
        lat: sumLat / count,
        lng: sumLng / count
    };
};

/**
 * Buyer zones are PERSISTED with `geometry` JSON-stringified — both write paths
 * do it (functions/utils/buyerValidation.js sanitiseBuyerData, and the direct
 * Firestore writers in BuyBoxForm/Buyers/BuyerDetail).
 *
 * Readers see one of two shapes depending on how they load the buyer:
 *   - RAW STRING  — anything reading Firestore directly (getBuyer/getBuyers).
 *   - OBJECT      — getPublicDealFeed parses before returning buyerCriteria.
 *
 * That split is the hazard. A string `geometry` fails sanitiseBuyerData's
 * `p.geometry?.type === 'Polygon'` filter, so posting one back through the PUT
 * endpoint drops the zone with no error — no exception, no validation failure,
 * the zones are just gone. These helpers are idempotent across both shapes so
 * no caller has to know which one it got.
 *
 * @param {Object|string|null} geometry - GeoJSON geometry, or its JSON string
 * @returns {Object|null} - parsed geometry, or null if absent/unparseable
 */
export const parseZoneGeometry = (geometry) => {
    if (typeof geometry !== 'string') return geometry || null;
    try {
        return JSON.parse(geometry) || null;
    } catch {
        // A malformed string is dropped rather than thrown: these helpers run
        // inside seed effects, and throwing there would blank the whole form.
        return null;
    }
};

const toZoneArray = (polygons) => (Array.isArray(polygons) ? polygons : []);

let zoneKeySeq = 0;

/**
 * Normalise stored zones into the shape the editors and BuyerZoneDrawMap want:
 * parsed `geometry` plus a stable `_key` for React list identity (stored zones
 * have no id of their own — they're positional entries in an array).
 *
 * @param {Array} polygons - zones as read from Firestore
 * @returns {Array} - zones with object geometry and a _key
 */
export const normalizeZonesForEditing = (polygons) => (
    toZoneArray(polygons).map((p) => ({
        ...p,
        geometry: parseZoneGeometry(p?.geometry),
        _key: p?._key || `p${Date.now().toString(36)}${(zoneKeySeq++).toString(36)}`,
    }))
);

/**
 * A zone with no usable geometry cannot be written (a null geometry would just
 * persist a corrupt zone), so the serialisers below drop it. Dropping zones
 * SILENTLY is the exact bug this module exists to prevent, so say so loudly —
 * this should never fire, and if it does we want it in the console, not
 * discovered later as missing zones.
 */
function warnOnDroppedZones(input, output, fnName) {
    if (output.length < input.length) {
        console.warn(
            `[geoUtils] ${fnName} dropped ${input.length - output.length} of ${input.length} ` +
            `buyer zone(s) with missing or unparseable geometry — they will not be saved.`
        );
    }
    return output;
}

/**
 * Serialise zones back to the STORED shape (stringified geometry) for callers
 * that write Firestore directly — BuyBoxForm, Buyers, BuyerDetail.
 *
 * Do NOT use this for the PUT endpoint (updatePublicBuyer): sanitiseBuyerData
 * requires an OBJECT geometry and stringifies it itself — handing it a string
 * is exactly the bug this module exists to prevent. Use
 * serializeZonesForValidation for that path.
 *
 * @param {Array} polygons - zones from editor state
 * @returns {Array} - zones with geometry as a JSON string
 */
export const serializeZonesForStorage = (polygons) => {
    const zones = toZoneArray(polygons);
    return warnOnDroppedZones(zones, zones
        .filter(p => p?.geometry)
        .map(p => ({
            name: p.name || null,
            active: p.active !== false,
            geometry: typeof p.geometry === 'string' ? p.geometry : JSON.stringify(p.geometry),
        })), 'serializeZonesForStorage');
};

/**
 * Serialise zones for the server-validated PUT path, which expects an OBJECT
 * geometry (sanitiseBuyerData stringifies on the way to Firestore).
 *
 * @param {Array} polygons - zones from editor state
 * @returns {Array} - zones with geometry as a parsed object
 */
export const serializeZonesForValidation = (polygons) => {
    const zones = toZoneArray(polygons);
    return warnOnDroppedZones(zones, zones
        .map(p => ({
            name: p?.name || null,
            active: p?.active !== false,
            geometry: parseZoneGeometry(p?.geometry),
        }))
        .filter(p => p.geometry), 'serializeZonesForValidation');
};

/**
 * Calculate bounds for a polygon (for map fitting)
 * @param {Object} geoJSONGeometry - GeoJSON Polygon geometry
 * @returns {Object|null} - { north, south, east, west } bounds or null
 */
export const getPolygonBounds = (geoJSONGeometry) => {
    if (!geoJSONGeometry || !geoJSONGeometry.coordinates) {
        return null;
    }

    const points = [];
    const { type, coordinates } = geoJSONGeometry;

    if (type === 'Polygon') {
        for (const ring of coordinates) {
            if (!Array.isArray(ring)) continue;
            for (const coord of ring) {
                if (
                    Array.isArray(coord) &&
                    Number.isFinite(coord[0]) &&
                    Number.isFinite(coord[1])
                ) {
                    points.push(coord);
                }
            }
        }
    } else if (type === 'MultiPolygon') {
        for (const polygonCoords of coordinates) {
            if (!Array.isArray(polygonCoords)) continue;
            for (const ring of polygonCoords) {
                if (!Array.isArray(ring)) continue;
                for (const coord of ring) {
                    if (
                        Array.isArray(coord) &&
                        Number.isFinite(coord[0]) &&
                        Number.isFinite(coord[1])
                    ) {
                        points.push(coord);
                    }
                }
            }
        }
    } else {
        return null;
    }

    if (points.length === 0) {
        return null;
    }

    let north = -Infinity;
    let south = Infinity;
    let east = -Infinity;
    let west = Infinity;

    for (const [lng, lat] of points) {
        if (lat > north) north = lat;
        if (lat < south) south = lat;
        if (lng > east) east = lng;
        if (lng < west) west = lng;
    }

    if (
        !Number.isFinite(north) ||
        !Number.isFinite(south) ||
        !Number.isFinite(east) ||
        !Number.isFinite(west)
    ) {
        return null;
    }

    return { north, south, east, west };
};

/* ------------------------------------------------------------------------- *
 * Freehand stroke processing
 *
 * A finger traced across a map produces hundreds of near-duplicate points at
 * whatever rate the browser fires pointermove. That raw path is unusable: it
 * bloats the stored geometry, slows every downstream point-in-polygon test, and
 * frequently self-intersects where the user wobbled. These helpers turn a raw
 * screen-space stroke into a ring worth saving.
 *
 * Everything here works in *screen pixels*, deliberately. Simplifying before
 * projecting is both cheaper and resolution-independent: a 2px tolerance means
 * the same visual fidelity whether the user is zoomed to a city or a block,
 * whereas a fixed degree tolerance would over-simplify when zoomed in and
 * under-simplify when zoomed out.
 * ------------------------------------------------------------------------- */

// Perpendicular distance from p to the infinite line through a and b.
const perpendicularDistance = (p, a, b) => {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    if (dx === 0 && dy === 0) return Math.hypot(p.x - a.x, p.y - a.y);
    // |cross product| / |ab|
    return Math.abs(dy * p.x - dx * p.y + b.x * a.y - b.y * a.x) / Math.hypot(dx, dy);
};

/**
 * Ramer–Douglas–Peucker simplification of a screen-space path.
 *
 * Iterative rather than recursive: a long stroke can run to thousands of points
 * and the naive recursion blows the stack on the degenerate near-straight case.
 *
 * @param {Array<{x: number, y: number}>} pts - raw stroke points
 * @param {number} tolerance - max deviation in pixels
 * @returns {Array<{x: number, y: number}>}
 */
export const simplifyScreenPath = (pts, tolerance = 2) => {
    if (!Array.isArray(pts) || pts.length < 3) return Array.isArray(pts) ? [...pts] : [];

    const keep = new Uint8Array(pts.length);
    keep[0] = 1;
    keep[pts.length - 1] = 1;

    const stack = [[0, pts.length - 1]];
    while (stack.length > 0) {
        const [first, last] = stack.pop();
        let maxDist = 0;
        let index = -1;
        for (let i = first + 1; i < last; i++) {
            const dist = perpendicularDistance(pts[i], pts[first], pts[last]);
            if (dist > maxDist) {
                maxDist = dist;
                index = i;
            }
        }
        if (maxDist > tolerance && index !== -1) {
            keep[index] = 1;
            stack.push([first, index], [index, last]);
        }
    }

    const out = [];
    for (let i = 0; i < pts.length; i++) {
        if (keep[i]) out.push(pts[i]);
    }
    return out;
};

/**
 * Simplify to a vertex budget, coarsening the tolerance until the path fits.
 *
 * simplifyScreenPath's tolerance is expressed in the same frame as the points,
 * which is fine when that frame is the one the user drew in. Freehand zone
 * drawing breaks that assumption: pinching mid-stroke means the shape is
 * measured in an inflated frame, where a 2px tolerance removes almost nothing
 * (measured on a traced loop: 42 points normally, 108 after two zoom levels of
 * pinch, 308 after eight). The survivors are persisted per zone and walked for
 * every lead during buyer matching, so an unbounded ring is an ongoing cost.
 *
 * The first pass runs at the caller's tolerance, so a path already within
 * budget is returned exactly as simplifyScreenPath would have returned it —
 * this is a backstop, not a change in behaviour for the ordinary case.
 *
 * @param {Array<{x: number, y: number}>} pts - raw path points
 * @param {number} tolerance - starting max deviation, in the points' own frame
 * @param {number} maxVertices - ceiling on the returned point count
 * @returns {Array<{x: number, y: number}>} original point objects, subset
 */
export const simplifyToVertexBudget = (pts, tolerance = 2, maxVertices = 150) => {
    let simplified = simplifyScreenPath(pts, tolerance);
    if (!Number.isFinite(maxVertices) || maxVertices < 3) return simplified;

    // Doubling converges fast — even a 4096x inflated frame is back under
    // budget in ~9 rounds. The ceiling is a termination guard, not a target.
    let current = tolerance;
    while (simplified.length > maxVertices && current < 1e7) {
        current *= 2;
        const next = simplifyScreenPath(pts, current);
        // A big enough tolerance collapses the path to its two endpoints. Keep
        // the last result that was still a usable ring: returning the collapsed
        // one would surface as "too short to be an area" on a shape whose only
        // problem was having too many points.
        if (next.length < 3) return simplified;
        simplified = next;
    }
    return simplified;
};

// Do segments p1→p2 and p3→p4 cross? Shared endpoints don't count — adjacent
// ring segments always touch and that is not an intersection.
const segmentsCross = (p1, p2, p3, p4) => {
    const orient = (a, b, c) => {
        const val = (b.y - a.y) * (c.x - b.x) - (b.x - a.x) * (c.y - b.y);
        if (Math.abs(val) < 1e-9) return 0;
        return val > 0 ? 1 : 2;
    };
    const o1 = orient(p1, p2, p3);
    const o2 = orient(p1, p2, p4);
    const o3 = orient(p3, p4, p1);
    const o4 = orient(p3, p4, p2);
    return o1 !== o2 && o3 !== o4;
};

/**
 * True when a closed ring crosses itself (a figure-eight).
 *
 * Worth rejecting rather than repairing: a self-intersecting polygon has no
 * well-defined interior, so point-in-polygon tests against it silently return
 * nonsense and the buy box would quietly match the wrong properties.
 *
 * O(n²), which is fine — call this only after simplifyScreenPath has taken the
 * ring down to tens of points.
 *
 * @param {Array<{x: number, y: number}>} ring - open ring (last point != first)
 * @returns {boolean}
 */
export const ringSelfIntersects = (ring) => {
    if (!Array.isArray(ring)) return false;

    // Normalise away an explicitly-closed ring. A finger that finishes where it
    // started leaves first === last, and the duplicate shifts every adjacency
    // index below — segment 0 (A→B) and the second-to-last (D→A) then look like
    // an intersection instead of two segments sharing a vertex. That false
    // positive rejects perfectly good zones, so strip the duplicate first.
    let pts = ring;
    while (
        pts.length > 1 &&
        pts[0]?.x === pts[pts.length - 1]?.x &&
        pts[0]?.y === pts[pts.length - 1]?.y
    ) {
        pts = pts.slice(0, -1);
    }

    if (pts.length < 4) return false;
    ring = pts;
    const n = ring.length;
    for (let i = 0; i < n; i++) {
        const a1 = ring[i];
        const a2 = ring[(i + 1) % n];
        for (let j = i + 1; j < n; j++) {
            // Skip adjacent segments (and the wrap-around pair), which share a
            // vertex by construction.
            if (j === i || (j + 1) % n === i || (i + 1) % n === j) continue;
            if (segmentsCross(a1, a2, ring[j], ring[(j + 1) % n])) return true;
        }
    }
    return false;
};

/**
 * Absolute area of a screen-space ring, in square pixels (shoelace).
 *
 * Used to reject accidental taps and tiny scribbles before they become zones.
 *
 * @param {Array<{x: number, y: number}>} ring
 * @returns {number}
 */
export const ringAreaPx = (ring) => {
    if (!Array.isArray(ring) || ring.length < 3) return 0;
    let sum = 0;
    for (let i = 0; i < ring.length; i++) {
        const a = ring[i];
        const b = ring[(i + 1) % ring.length];
        sum += (a.x * b.y) - (b.x * a.y);
    }
    return Math.abs(sum) / 2;
};
